import React, { memo, useCallback, useEffect, useState } from 'react'
import { Howl } from "howler";

// constants
import { IMAGE_TYPES } from '../constants';

// assets
import shutter from "../../../assets/media/camera-shutter-click_v3.mp3";

var shutterSound = new Howl({
    src: [shutter],
});

function ImageCapture(props) {
    const {
        imageTypeToCapture,
        setCapturedImage,
        capturedImage,
        onNext,
        isImageStreamLoaded,
        setIsImageStreamLoaded
    } = props;

    const [videoRef, setVideoRef] = useState(null);
    const [canvasRef, setCanvasRef] = useState(null);
    const [captureErrors, setCaptureErrors] = useState(null);
    const [retryTrigger, setRetryTrigger] = useState(false);

    const captureImage = useCallback(() => {
        setTimeout(function () {
            shutterSound.stop();
            shutterSound.play();
        }, 100);
        const video = videoRef;
        const canvas = document.createElement('canvas');

        // Set canvas size to match video size
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Draw the current video frame onto the canvas
        const context = canvas.getContext("2d");
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert canvas content to a data URL (base64)
        const imageDataUrl = canvas.toDataURL("image/jpeg");
        setCapturedImage(imageDataUrl);
    }, [setCapturedImage, videoRef])

    const drawGuidelines = useCallback((imageTypeToCapture) => {
        if (!canvasRef) return;

        const canvas = canvasRef;
        const context = canvas.getContext("2d");

        // Clear the canvas before drawing new guidelines
        context.clearRect(0, 0, canvas.width, canvas.height);

        if (imageTypeToCapture !== IMAGE_TYPES.CROWN) {
            return;
        }

        // Set styles for the global image guidelines
        context.strokeStyle = "red"; // Semi-transparent red
        context.lineWidth = 2;

        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2.75;
        const radius = Math.min(canvas.width, canvas.height) / 2.25; // Circle radius (adjust as needed)

        // Draw the circle
        context.beginPath();
        context.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        context.stroke();

        // Draw the vertical diameter
        context.beginPath();
        context.moveTo(centerX, centerY - radius);
        context.lineTo(centerX, centerY + radius);
        context.stroke();
    }, [canvasRef]);


    useEffect(() => {
        if (!videoRef) return;

        setCaptureErrors(null);

        const initCamera = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: { ideal: "environment" },
                        width: { ideal: 3840 }, // Try 4K resolution
                        height: { ideal: 2160 }
                    }
                });
                videoRef.srcObject = stream;

                // Explicitly play the video
                await videoRef.play().catch((err) => {
                    console.error("Error playing video:", err.message);
                    setCaptureErrors("Unable to play the video: " + err.message);
                });
            } catch (err) {
                console.error("Error accessing back camera:", err);
                setCaptureErrors("Error accessing back camera: " + (typeof err === "string" ? err : err.message))
            }
        };
        initCamera();

        return () => {
            if (videoRef && videoRef.srcObject) {
                const stream = videoRef.srcObject;
                const tracks = stream.getTracks();
                tracks.forEach(track => track.stop());
            }
        };
    }, [videoRef, retryTrigger]);

    useEffect(() => {
        if (videoRef && canvasRef) {
            const handleLoadedMetadata = () => {
                // Set canvas dimensions to match video dimensions
                const videoAspectRatio = videoRef.videoWidth / videoRef.videoHeight;
                const container = videoRef.parentNode;
                const containerWidth = container.clientWidth;
                const containerHeight = container.clientHeight;

                // Calculate dimensions to maintain aspect ratio within the container
                if (containerWidth / containerHeight > videoAspectRatio) {
                    canvasRef.width = containerHeight * videoAspectRatio;
                    canvasRef.height = containerHeight;
                } else {
                    canvasRef.width = containerWidth;
                    canvasRef.height = containerWidth / videoAspectRatio;
                }

                setIsImageStreamLoaded(true)
            };

            videoRef.addEventListener('loadedmetadata', handleLoadedMetadata);

            return () => {
                videoRef.removeEventListener('loadedmetadata', handleLoadedMetadata);
            };
        }
    }, [canvasRef, setIsImageStreamLoaded, videoRef]);

    useEffect(() => {
        if (isImageStreamLoaded) {
            drawGuidelines(imageTypeToCapture);
        }
    }, [isImageStreamLoaded, drawGuidelines, imageTypeToCapture]);


    return (
        <div className='flex-grow flex flex-col pb-10'>
            {captureErrors && (
                <div className='flex flex-col gap-3 items-center'>
                    <p className='text-red-400 font-bold text-sm text-center'>{captureErrors}</p>
                    <button
                        className="px-8 py-3 bg-red-700 w- font-bold text-xs rounded-lg text-white cursor-pointer uppercase focus:outline-none"
                        onClick={() => setRetryTrigger((prev) => !prev)}

                    >
                        Retry
                    </button>
                </div>
            )}
            <section className='flex-grow w-auto text-black flex items-start justify-center rounded-2xl'>
                <div
                    className="w-[360px] h-auto overflow-hidden relative rounded-2xl"
                >
                    <video
                        ref={setVideoRef}
                        autoPlay
                        style={{
                            width: 360,
                            height: 500,
                            objectFit: "cover",
                            zIndex: 3,
                        }}
                        playsInline
                    />
                    <canvas
                        ref={setCanvasRef}
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            pointerEvents: "none",
                            zIndex: 5,
                        }}
                    />
                    {!!capturedImage && (
                        <img src={capturedImage} className="absolute w-full h-full top-0 left-0 z-20 object-cover" alt="captured resource" />
                    )}
                    {!captureErrors && isImageStreamLoaded && (
                        <>
                            {Boolean(capturedImage) ? (
                                <div className='absolute bottom-0 left-1/2 -translate-y-[10px] -translate-x-1/2 z-30 flex items-center justify-center gap-5'>
                                    <div
                                        onClick={() => setCapturedImage(null)}
                                        className='bg-[#f4831f]/50 rounded-full p-1.5 h-16 w-16 flex items-center justify-center'>
                                        <div className='w-full h-full flex items-center justify-center bg-[#f4831f] rounded-full'>
                                            <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M25.4355 4.16784C26.2287 3.33885 27.0283 2.51614 27.8121 1.67853C28.2072 1.25581 28.6594 1.13581 29.1921 1.33973C29.7507 1.55383 29.9972 1.94127 29.9979 2.59457C30.0003 5.44386 30.0011 8.29315 29.9979 11.1424C29.9972 11.98 29.491 12.4859 28.6539 12.4859C25.7984 12.4883 22.943 12.4875 20.0875 12.4843C19.4249 12.4835 19.0869 12.249 18.8405 11.6734C18.6042 11.1205 18.7889 10.6923 19.1863 10.3001C19.9757 9.52054 20.7564 8.73234 21.5395 7.94728C21.6209 7.86571 21.696 7.77866 21.7945 7.672C21.1124 7.00536 20.3574 6.47597 19.5289 6.06187C13.9393 3.26591 6.75916 6.16461 5.25554 12.8663C4.02026 18.3711 7.61111 23.8454 13.279 24.8101C17.0717 25.4555 20.2682 24.2305 22.7607 21.2565C23.1816 20.7546 23.5219 20.7342 23.9952 21.2094C24.7877 22.0047 25.5786 22.8007 26.368 23.5991C26.7967 24.0328 26.8061 24.2658 26.4016 24.7411C24.7791 26.6484 22.7959 28.0546 20.4669 28.9573C17.8532 29.9714 15.1441 30.2796 12.3989 29.7322C7.21603 28.6985 3.45699 25.7881 1.27824 20.9232C0.0578181 18.2002 -0.307525 15.3383 0.263568 12.4169C1.28058 7.21241 4.20489 3.45806 9.05214 1.27228C11.7926 0.036253 14.6645 -0.282949 17.617 0.244087C20.4278 0.746026 22.8992 1.97578 25.0412 3.86903C25.1092 3.92942 25.1765 3.99216 25.2461 4.05098C25.2649 4.06666 25.2923 4.07372 25.3158 4.08392C25.3557 4.11215 25.3956 4.14039 25.4355 4.16784Z" fill="white" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div
                                        onClick={onNext}
                                        className='bg-[#f4831f]/50 rounded-full p-1.5 h-16 w-16 flex items-center justify-center'>
                                        <div className='w-full h-full flex items-center justify-center bg-[#f4831f] rounded-full'>
                                            <svg width="30" height="30" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z" fill='white' /></svg>
                                        </div>

                                    </div>
                                </div>
                            ) : (
                                <div onClick={captureImage} className='absolute bottom-0 left-1/2 -translate-y-[10px] -translate-x-1/2 z-30 bg-[#f4831f]/50 rounded-full p-1.5 h-16 w-16 flex items-center justify-center'>
                                    <div className='w-full h-full bg-[#f4831f] rounded-full'>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section >
        </div>
    )
}

export default memo(ImageCapture)